import { useState } from 'react'
import * as planningApi from '../../../../lib/api/v2/planning'
import type { PlanningAuditEntryDto, ScheduleEventKind } from '../../../../lib/api/v2/planning'
import { formatDate } from '../../../../lib/format'
import { kindLabel } from '../planning-helpers'

interface HistoryAccordionProps {
  projectId: string
  userNameById?: Record<string, string>
}

/**
 * FR label per audit ChangeType (codes are English, UI is French). Kept in sync with the change types
 * the BE actually emits (ScheduleEventService / WorkItemCommandService / PlanningConstraintService).
 * Slot confirmation is recorded on the event (« EventClientConfirmed »), not per work item — that
 * code keeps its historical name on purpose: thousands of rows already carry it, and renaming it
 * with the SOLY-PLAN-002 columns would have split the history in two vocabularies.
 *
 * ⚠️ A code missing from this table renders RAW — the user reads « EventKindConverted » in a French
 * history. Any new ChangeType emitted by the BE must land here in the same PR.
 */
const CHANGE_LABELS: Record<string, string> = {
  EventCreated: 'Événement planifié',
  EventRescheduled: 'Événement déplacé',
  EventCancelled: 'Événement annulé',
  EventClientConfirmed: 'Date confirmée',
  // SOLY-PLAN-002
  EventConfirmationCleared: 'Créneau remis en proposé',
  EventKindConverted: 'Type de créneau modifié',
  EventAssigned: 'Assignation',
  EventUnassigned: 'Désassignation',
  // SOLY-PLAN-004
  EventNoteChanged: 'Note du créneau',
  SurveyReport: 'Rapport métré',
  SurveyNotApplicable: 'Métré non nécessaire',
  SurveyReactivated: 'Métré réactivé',
  SurveyCancelled: 'Métré annulé',
  SurveyEstimate: 'Estimation métré',
  InstallationStarted: 'Pose démarrée',
  InstallationReport: 'Rapport pose',
  InstallationNotApplicable: 'Pose non nécessaire',
  InstallationReactivated: 'Pose réactivée',
  InstallationCancelled: 'Pose annulée',
  InstallationEstimate: 'Estimation pose',
  InstallationNotes: 'Note de pose',
  ConstraintOverridden: 'Délai ajusté',
  ConstraintReset: 'Délai réinitialisé',
  // Écrits sous AggregateType « Project », donc rendus dans CE MÊME accordéon — ils manquaient
  // depuis leur introduction et s'affichaient en anglais brut.
  InterventionCreated: 'Intervention ajoutée',
  PlanningInitialized: 'Suivi de chantier activé',
}

/**
 * FR label for an audit code, falling back to the raw code. Exported for the test that pins the
 * codes this feature added — a missing entry is invisible until a user reads English in the
 * history, so it needs an assertion rather than vigilance.
 */
export const changeTypeLabel = (code: string) => CHANGE_LABELS[code] ?? code

const label = changeTypeLabel

/**
 * Les valeurs avant/après sont écrites par le backend en anglais brut quand ce sont des codes.
 * `EventKindConverted` est le premier ChangeType à en porter DEUX (`EventRescheduled` passe par
 * `FormatSlot`, déjà lisible) : sans traduction, l'historique affiche « Type de créneau modifié —
 * Appointment → InternalWork », c'est-à-dire exactement le vocabulaire que ce lot remplace.
 *
 * Un code inconnu ressort tel quel : mieux vaut une valeur brute qu'une valeur inventée.
 */
const KIND_CODES: readonly string[] = ['TechnicalSurvey', 'Installation', 'Appointment', 'InternalWork']

export function changeValue(changeType: string, value: string): string {
  if (changeType !== 'EventKindConverted') return value
  return KIND_CODES.includes(value) ? kindLabel(value as ScheduleEventKind) : value
}

/**
 * Lazy-loaded "Historique" accordion for the project sidebar (SOLY-PLAN-001 MS-15). Fetches the
 * planning audit trail for the project on first expand; newest first; empty state when nothing
 * has changed yet.
 */
export function HistoryAccordion({ projectId, userNameById = {} }: HistoryAccordionProps) {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<PlanningAuditEntryDto[] | null>(null)
  const [loading, setLoading] = useState(false)

  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (next && entries === null) {
      setLoading(true)
      try {
        setEntries(await planningApi.getProjectAudit(projectId))
      } catch {
        setEntries([])
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <section className="rounded-lg border p-2" data-testid="planning-step-historique">
      <button
        type="button"
        onClick={() => void toggle()}
        className="flex w-full items-center justify-between text-sm font-medium"
        data-testid="planning-history-toggle"
        aria-expanded={open}
      >
        <span>Historique</span>
        <span className="text-xs text-muted-foreground">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="mt-2 space-y-2" data-testid="planning-history-list">
          {loading && <p className="text-xs text-muted-foreground">Chargement…</p>}
          {!loading && entries?.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="planning-history-empty">Aucun changement enregistré</p>
          )}
          {!loading && entries?.map((e) => (
            <div key={e.id} className="border-l-2 border-muted pl-2 text-xs" data-testid={`planning-history-entry-${e.id}`}>
              <div className="font-medium">{label(e.changeType)}</div>
              <div className="text-muted-foreground">
                {formatDate(e.at)}
                {userNameById[e.byUserId] ? ` · ${userNameById[e.byUserId]}` : ''}
                {e.reason ? ` — ${e.reason}` : ''}
              </div>
              {/* One side is enough to be worth showing. It used to take BOTH, so an entry whose
                  value was created or cleared rendered its date and nothing else — and on a note,
                  the value IS the content: « Note du créneau · 18/08 » said when something changed
                  without ever saying what (SOLY-PLAN-004). « — » stands for the empty side. */}
              {(e.beforeValue || e.afterValue) && (
                <div className="text-muted-foreground">
                  {e.beforeValue ? changeValue(e.changeType, e.beforeValue) : '—'}
                  {' → '}
                  {e.afterValue ? changeValue(e.changeType, e.afterValue) : '—'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
